import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { usePainelExecutivoData } from './hooks/usePainelExecutivoData';
import { filtrarFaturas } from './lib/filtrarFaturas';
import { calcularKpis } from '../../../src/lib/financeiro/calcularKpis';
import { calcularAging } from '../../../src/lib/financeiro/calcularAging';
import { calcularPontosMensais } from '../../../src/lib/financeiro/calcularPontosMensais';
import { DEFINICOES_KPI, calcularTopDevedores, faturasDoPainel } from '../../../src/lib/financeiro/definicoesKpi';
import type { IdKpi } from '../../../src/lib/financeiro/definicoesKpi';
import { calcularComparativoMensal } from './lib/calcularComparativos';
import { calcularInsights } from './lib/calcularInsights';
import {
  planilhaAging, planilhaBoletos, planilhasDeBoletos, planilhaEvolucaoMensal, planilhaResumo, planilhaSobreLista, planilhaTopDevedores, slugArquivo,
} from './lib/planilhasPainel';
import type { FiltrosDescritos } from './lib/planilhasPainel';
import { baixarPlanilhas } from './lib/baixarPlanilhas';
import { useAcoesBoleto } from './hooks/useAcoesBoleto';
import { useIndiceNomesUc } from '../../../src/hooks/useIndiceNomesUc';
import { useToast } from '../../../src/contexts/ToastContext';
import { FILTROS_INICIAIS } from './types';
import type { DevedorTop, FaturaPainel, PontoMensal } from './types';
import LoadingSpinner from '../../../src/components/LoadingSpinner';
import FaixaMarca from './components/FaixaMarca';
import FiltrosGlobais, { rotuloPeriodo } from './components/FiltrosGlobais';
import KpiCards from './components/KpiCards';
import InsightsAutomaticos from './components/InsightsAutomaticos';
import EvolucaoMensalChart from './components/EvolucaoMensalChart';
import EvolucaoInadimplenciaChart from './components/EvolucaoInadimplenciaChart';
import BoletosPorStatusDonut from './components/BoletosPorStatusDonut';
import AgingTable from './components/AgingTable';
import TopDevedoresTable from './components/TopDevedoresTable';
import ComparativoPeriodos from './components/ComparativoPeriodos';
import ListaBoletosDrawer from './components/ListaBoletosDrawer';
import UCManagementModal from '../UCManagementModal';

const MAX_MESES_GRAFICO = 13;
const TOP_DEVEDORES_EXCEL = 50;

const PainelExecutivo: React.FC = () => {
  const { dados, loading, erro, ultimaAtualizacao, refetch } = usePainelExecutivoData();
  const { addToast } = useToast();
  const [filtros, setFiltros] = useState(FILTROS_INICIAIS);
  const hoje = useMemo(() => new Date(), []);

  const [kpiAberto, setKpiAberto] = useState<IdKpi | null>(null);
  const { ucAberta, setUcAberta, abrindoUcId, abrindoBoletoId, abrirUc, abrirBoleto } = useAcoesBoleto();
  // Assinante e titular da conta de luz — o cadastro da UC é a única fonte do titular.
  const nomes = useIndiceNomesUc();
  const [exportandoPainel, setExportandoPainel] = useState(false);
  const [exportandoLista, setExportandoLista] = useState(false);

  const filtrados = useMemo(() => filtrarFaturas(dados, filtros, hoje), [dados, filtros, hoje]);
  const kpis = useMemo(() => calcularKpis(filtrados, hoje), [filtrados, hoje]);
  const aging = useMemo(() => calcularAging(filtrados, hoje), [filtrados, hoje]);

  // Os comparativos passam a respeitar os filtros do topo — até aqui liam `dados`
  // cru, então com uma UG filtrada o VALOR do card era da UG e o PERCENTUAL era
  // da carteira inteira (solicitação 5842).
  //
  // ⚠️ O filtro de STATUS fica de fora, de propósito. `statusDerivado` é sempre
  // "hoje": filtrar por ele e depois perguntar "como estava há 30 dias" some
  // justamente com quem mudou de estado no período — um boleto que estava
  // vencido e foi pago sairia dos dois lados da conta, e a dívida de 30 dias
  // atrás apareceria menor do que foi, invertendo o sinal da variação.
  const dadosComparativo = useMemo(
    () => filtrarFaturas(dados, { ...filtros, status: 'todos' }, hoje),
    [dados, filtros, hoje]
  );

  const comparativoInadimplenciaMensal = useMemo(
    () => calcularComparativoMensal(dadosComparativo, 'inadimplencia', hoje),
    [dadosComparativo, hoje]
  );

  const maiorDevedor: DevedorTop | null = useMemo(() => calcularTopDevedores(filtrados, 1)[0] ?? null, [filtrados]);

  const temVencidoMaisDe90Dias = useMemo(() => aging.find((f) => f.faixa === '+90')!.qtde > 0, [aging]);

  const insights = useMemo(
    () => calcularInsights({ kpis, comparativoInadimplenciaMensal, maiorDevedor, temVencidoMaisDe90Dias }),
    [kpis, comparativoInadimplenciaMensal, maiorDevedor, temVencidoMaisDe90Dias]
  );

  const pontosMensaisTodos: PontoMensal[] = useMemo(() => calcularPontosMensais(filtrados), [filtrados]);
  const pontosMensais: PontoMensal[] = useMemo(() => {
    // O corte de MAX_MESES_GRAFICO existe para não poluir o gráfico nos filtros
    // relativos (últimos 12 meses, trimestre etc.), que já vêm com janela curta.
    // Em "Todo o histórico" a janela é livre (calcularJanelaPeriodo retorna null) e
    // pode incluir meses futuros já emitidos antecipadamente — cortar aqui pelos
    // "últimos 13 da lista ordenada" empurraria meses históricos reais para fora
    // do gráfico só porque meses futuros ocupam vagas na janela. Por isso não
    // aplicamos o corte quando o filtro é "historico": queremos o período real.
    if (filtros.periodo === 'historico') return pontosMensaisTodos;
    return pontosMensaisTodos.slice(-MAX_MESES_GRAFICO);
  }, [pontosMensaisTodos, filtros.periodo]);

  // A lista do card sai da MESMA definição que calculou o número do card — e do
  // MESMO recorte: `calcularKpis`/`calcularAging`/`calcularTopDevedores` tiram a
  // conta interna (Associação Vigor) antes de somar, então a lista precisa do
  // mesmo `faturasDoPainel` ou o total do drawer não bate com o do card
  // (solicitação 5955).
  const faturasDoCard = useMemo(
    () => (kpiAberto ? DEFINICOES_KPI[kpiAberto].filtrar(faturasDoPainel(filtrados)) : []),
    [kpiAberto, filtrados]
  );

  const filtrosDescritos: FiltrosDescritos = useMemo(() => ({
    periodo: rotuloPeriodo(filtros.periodo),
    unidadeGeradora: filtros.unidadeGeradora === 'todas' ? 'Todas as UGs'
      : filtros.unidadeGeradora === 'outras' ? 'Outras / sem UG' : filtros.unidadeGeradora,
    status: filtros.status === 'todos' ? 'Todos' : filtros.status,
    cliente: filtros.clienteBusca.trim(),
  }), [filtros]);

  const exportarPainel = async () => {
    setExportandoPainel(true);
    try {
      const agora = new Date();
      await baixarPlanilhas([
        planilhaResumo(kpis, filtrosDescritos, agora),
        ...planilhasDeBoletos(filtrados, nomes),
        planilhaEvolucaoMensal(pontosMensaisTodos),
        planilhaAging(aging),
        planilhaTopDevedores(calcularTopDevedores(filtrados, TOP_DEVEDORES_EXCEL)),
      ], `painel-financeiro_${slugArquivo(filtrosDescritos.periodo)}`);
    } catch (e: any) {
      console.error('Erro ao exportar o Painel Financeiro Executivo:', e);
      addToast(e?.message || 'Não foi possível gerar o Excel.', 'error');
    } finally {
      setExportandoPainel(false);
    }
  };

  const exportarLista = async (faturasVisiveis: FaturaPainel[]) => {
    if (!kpiAberto) return;
    const def = DEFINICOES_KPI[kpiAberto];
    setExportandoLista(true);
    try {
      await baixarPlanilhas([
        planilhaBoletos(faturasVisiveis, 'Boletos', undefined, nomes),
        planilhaSobreLista(def.titulo, def.descricaoLista, filtrosDescritos, new Date()),
      ], `boletos_${slugArquivo(def.titulo)}_${slugArquivo(filtrosDescritos.periodo)}`);
    } catch (e: any) {
      console.error('Erro ao exportar a lista de boletos:', e);
      addToast(e?.message || 'Não foi possível gerar o Excel.', 'error');
    } finally {
      setExportandoLista(false);
    }
  };

  const fecharLista = useCallback(() => setKpiAberto(null), []);

  // O auto-refresh de 10 min chama o mesmo `carregar` que a carga inicial. Se ele
  // falhar (rede instável, por exemplo), `erro` é setado do mesmo jeito — mas aqui
  // já existe painel na tela. Trocar tudo pela tela de erro faria quem está
  // analisando os números perder o contexto por uma falha de atualização em
  // segundo plano. Com dados carregados, o erro vira um aviso e os dados antigos
  // continuam visíveis; só sem nenhum dado ainda é que a tela de erro faz sentido.
  useEffect(() => {
    if (erro && dados.length > 0) {
      addToast(`Não foi possível atualizar o painel: ${erro}`, 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [erro]);

  if (erro && dados.length === 0) {
    return (
      <div className="animate-fade-in flex flex-col items-center justify-center py-24 gap-3">
        <p className="text-sm font-semibold text-red-600 dark:text-red-400">{erro}</p>
        <button onClick={refetch} className="px-4 py-2 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primary-dark transition-colors">
          Tentar novamente
        </button>
      </div>
    );
  }

  const defAberta = kpiAberto ? DEFINICOES_KPI[kpiAberto] : null;

  return (
    <div className="animate-fade-in space-y-6 pb-24">
      <FaixaMarca
        qtdeBoletosNoFiltro={filtrados.length}
        ultimaAtualizacao={ultimaAtualizacao}
        loading={loading}
        onAtualizar={refetch}
        exportando={exportandoPainel}
        onExportar={exportarPainel}
      />
      <FiltrosGlobais filtros={filtros} onChange={setFiltros} dados={dados} />

      {loading && dados.length === 0 ? (
        <LoadingSpinner label="Carregando painel financeiro..." />
      ) : (
        <>
          <KpiCards
            kpis={kpis}
            variacaoVencidoPct={comparativoInadimplenciaMensal.diferencaPercentual}
            rotuloPeriodo={rotuloPeriodo(filtros.periodo)}
            onAbrir={setKpiAberto}
          />
          <InsightsAutomaticos insights={insights} />
          <EvolucaoMensalChart pontos={pontosMensais} />
          <EvolucaoInadimplenciaChart pontos={pontosMensais} />
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <BoletosPorStatusDonut dados={filtrados} />
            <AgingTable faixas={aging} />
          </div>
          <TopDevedoresTable dados={filtrados} />
          <ComparativoPeriodos dados={dados} hoje={hoje} />
        </>
      )}

      <ListaBoletosDrawer
        aberto={!!defAberta}
        titulo={defAberta?.titulo ?? ''}
        descricao={defAberta?.descricaoLista ?? ''}
        faturas={faturasDoCard}
        hoje={hoje}
        mostrarDias={kpiAberto === 'prazoMedio' || kpiAberto === 'recuperado'}
        permitirFiltro7Dias={kpiAberto === 'aVencer'}
        escAtivo={!ucAberta}
        abrindoUcId={abrindoUcId}
        abrindoBoletoId={abrindoBoletoId}
        exportando={exportandoLista}
        onFechar={fecharLista}
        onAbrirUc={abrirUc}
        onAbrirBoleto={abrirBoleto}
        onExportar={exportarLista}
        nomes={nomes}
      />

      {/* O mesmo modal que Faturas/UCs, Esteira e Previsão abrem — com as ações da UC.
          Abre direto no histórico de faturas, que é o contexto de quem veio de um boleto. */}
      <UCManagementModal
        isOpen={!!ucAberta}
        onClose={() => setUcAberta(null)}
        unidade={ucAberta}
        abaInicial="historico_faturas"
        onSave={() => { setUcAberta(null); refetch(); }}
      />
    </div>
  );
};

export default PainelExecutivo;
